import { FC } from "react";
import { Box, Alert, CircularProgress } from "@mui/material";

import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams
} from "react-router-dom";
import { DepartmentProgress } from "./departmentProgress";
import { CommentsCard } from "../../../components/lifecycle/commentsCard";
import { InfoCard } from "./infoCard";
import { MarketAccess } from "./mftTasks/lifecycles/marketAccess";
import {
  Compliance,
  CreditRisk,
  Finance,
  Legal,
  Risk,
  Tech,
  Treasury
} from "./mftTasks";
import {
  useSpecificCounterparty,
  useUpdateCounterparty
} from "../dataHooks/counterpartyQueries";
import { CommentDto } from "../../../api/Api";
import { RequireLifecycleRole } from "../../../auth/RequireLifecycleRole";
import {
  canCommentCounterparty,
  isEntityVpEmail
} from "../../../auth/permissions";
import { EntityVpProvider } from "../../../auth/entityVpContext";
import { EntityMergerProvider } from "../../../auth/entityMergerContext";
import { useRoles } from "../../../auth/useRoles";
import { getCurrentCommentAuthor } from "../../../utils/commentUtils";
import { correctedEntityPath } from "../../../utils/entityPaths";

type Props = {};

type Params = {
  id?: string;
};

export const CounterParty: FC<Props> = () => {
  const { id } = useParams<Params>();
  const { pathname } = useLocation();
  const roles = useRoles();
  const canComment = canCommentCounterparty(roles);

  const {
    data: counterparty,
    isLoading,
    isFetching
  } = useSpecificCounterparty(id);

  const updateCounterparty = useUpdateCounterparty();

  const handleAddComment = (comment: CommentDto) => {
    if (!counterparty || !canComment) return;
    const updatedCounterparty = {
      ...counterparty,
      comments: [...(counterparty?.comments || []), comment]
    };
    updateCounterparty.mutate(updatedCounterparty);
  };

  const handleDeleteComment = (timestamp: string) => {
    if (!counterparty || !canComment) return;
    const updatedCounterparty = {
      ...counterparty,
      comments: (counterparty.comments || []).filter(
        (c) => c?.timestamp !== timestamp
      )
    };
    updateCounterparty.mutate(updatedCounterparty);
  };

  if (isLoading) {
    return <CircularProgress size={24} />;
  }

  if (counterparty === undefined) {
    return (
      <Box sx={{ padding: 3 }}>
        <Alert severity="error">Counterparty not found</Alert>
      </Box>
    );
  }

  // A merger opened under the TC tab (or the reverse) moves to the right tab, so the nav and every
  // link back to this page agree with what the entity is. Only once the loaded entity is the one
  // in the URL: keepPreviousData briefly shows the last one while the next loads.
  const correctedPath = correctedEntityPath(
    "counterparty",
    pathname,
    counterparty.isMerger
  );
  if (correctedPath && counterparty.id === id) {
    return <Navigate to={correctedPath} replace />;
  }

  // A VP holds no app role, so lifecycle access for them is decided per counterparty from the
  // vp field rather than from the token.
  const isEntityVp = isEntityVpEmail(
    counterparty?.vp,
    getCurrentCommentAuthor()
  );
  const isMerger = Boolean(counterparty.isMerger);

  return (
    <EntityVpProvider value={isEntityVp}>
      <EntityMergerProvider value={isMerger}>
        <Box flex={1} sx={{ overflowY: "auto" }}>
          <InfoCard specificCounterparty={counterparty} />
          <DepartmentProgress
            marketAccessProgress={counterparty?.progress?.marketAccess}
            complianceProgress={counterparty?.progress?.compliance}
            legalProgress={counterparty?.progress?.legal}
            riskProgress={counterparty?.progress?.risk}
            creditRiskProgress={counterparty?.progress?.creditRisk}
            treasuryProgress={counterparty?.progress?.treasury}
            financeProgress={counterparty?.progress?.finance}
            techProgress={counterparty?.progress?.tech}
          />
          <CommentsCard
            onAddComment={canComment ? handleAddComment : undefined}
            onDeleteComment={canComment ? handleDeleteComment : undefined}
            readOnly={!canComment}
            comments={(counterparty?.comments || []).filter(
              (comment): comment is CommentDto => Boolean(comment)
            )}
          />
          <Routes>
            {/* A merger has no Market Access lifecycle: its answers came in on the create form. */}
            {!isMerger && (
              <Route
                path="marketaccess"
                element={
                  <RequireLifecycleRole lifecycle="marketaccess">
                    <MarketAccess
                      specificCounterparty={counterparty}
                      key={counterparty?.id}
                    />
                  </RequireLifecycleRole>
                }
              />
            )}
            <Route
              path="compliance"
              element={
                <RequireLifecycleRole lifecycle="compliance">
                  <Compliance
                    specificCounterparty={counterparty}
                    key={counterparty?.id}
                  />
                </RequireLifecycleRole>
              }
            />
            <Route
              path="legal"
              element={
                <RequireLifecycleRole lifecycle="legal">
                  <Legal
                    specificCounterparty={counterparty}
                    key={counterparty?.id}
                  />
                </RequireLifecycleRole>
              }
            />
            <Route
              path="risk"
              element={
                <RequireLifecycleRole lifecycle="risk">
                  <Risk
                    specificCounterparty={counterparty}
                    key={counterparty?.id}
                  />
                </RequireLifecycleRole>
              }
            />
            <Route
              path="creditrisk"
              element={
                <RequireLifecycleRole lifecycle="creditrisk">
                  <CreditRisk
                    specificCounterparty={counterparty}
                    key={counterparty?.id}
                  />
                </RequireLifecycleRole>
              }
            />
            <Route
              path="treasury"
              element={
                <RequireLifecycleRole lifecycle="treasury">
                  <Treasury
                    specificCounterparty={counterparty}
                    key={counterparty?.id}
                  />
                </RequireLifecycleRole>
              }
            />
            <Route
              path="tech"
              element={
                <RequireLifecycleRole lifecycle="tech">
                  <Tech
                    specificCounterparty={counterparty}
                    key={counterparty?.id}
                  />
                </RequireLifecycleRole>
              }
            />
            <Route
              path="finance"
              element={
                <RequireLifecycleRole lifecycle="finance">
                  <Finance
                    specificCounterparty={counterparty}
                    key={counterparty?.id}
                  />
                </RequireLifecycleRole>
              }
            />
          </Routes>
          {isFetching && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 1000
              }}
            >
              <CircularProgress size={72} />
            </Box>
          )}
        </Box>
      </EntityMergerProvider>
    </EntityVpProvider>
  );
};
